import { ChangeEvent, useState, useCallback, KeyboardEventHandler, MutableRefObject } from 'react';
import { Button, EXTRA_SMALL, TERTIARY } from '@glu/buttons-react';
import locale from '@glu/locale';
import { Input } from '@glu/form-components';
import { DotDotDotAshIcon, ExclamationTriangleAshIcon } from '@glu/icons-react';
import Popover from '@glu/popover-react';
import classnames from 'classnames';
import { IView, ISavedViewProps } from '../SavedViews/SavedViews';
import useStyles from './ViewRow.styles';

export interface IViewRowProps {
  onSetActive(): void;
  view: IView;
  onActionKeyDown: KeyboardEventHandler<HTMLButtonElement>;
  onViewKeyDown: KeyboardEventHandler<HTMLButtonElement>;
  onRename: ISavedViewProps['onRenameSavedView'];
  onDelete: ISavedViewProps['onDeleteSavedView'];
  onSetDefault: ISavedViewProps['onSetDefaultSavedView'];
  popoverRef: MutableRefObject<HTMLElement | null>;
}

const popperOptions = {
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, -5],
      },
    },
  ],
};

const ViewRow = ({
  onActionKeyDown,
  onDelete,
  onRename,
  onSetActive,
  onSetDefault,
  onViewKeyDown,
  popoverRef,
  view,
}: IViewRowProps) => {
  const classes = useStyles();
  const [rowState, setRowState] = useState<'renaming' | 'deleting' | undefined>();
  const [newName, setNewName] = useState(view.name);

  const popoverContent = useCallback(
    ({ closePopover }) => {
      const handleActionMenuKeyDown = (event) => {
        const { key, target } = event;
        switch (key) {
          case 'ArrowDown':
            target.nextElementSibling?.focus();
            break;
          case 'ArrowUp':
            target.previousElementSibling?.focus();
            break;
          case 'Escape':
          case 'Tab':
          case 'ArrowLeft':
            event.preventDefault();
            document.getElementById(`${view.id}-action`)!.focus();
            closePopover();
            break;
          default:
        }
      };
      return (
        <>
          {!view.isDefault && (
            <Button
              className={classes.control}
              onClick={() => {
                onSetDefault({ view });
                closePopover();
              }}
              onKeyDown={handleActionMenuKeyDown}
              variant={TERTIARY}
            >
              {locale.get('dataComponents.setDefault')}
            </Button>
          )}
          {!view.isStatic && (
            <Button
              className={classes.control}
              onClick={() => {
                setRowState('renaming');
              }}
              onKeyDown={handleActionMenuKeyDown}
              variant={TERTIARY}
            >
              {locale.get('dataComponents.rename')}
            </Button>
          )}
          {!view.isStatic && (
            <Button
              className={classes.control}
              onClick={() => {
                setRowState('deleting');
              }}
              onKeyDown={handleActionMenuKeyDown}
              variant={TERTIARY}
            >
              {locale.get('dataComponents.delete')}
            </Button>
          )}
        </>
      );
    },
    [classes.control, onSetDefault, view]
  );
  switch (rowState) {
    case 'renaming':
      return (
        <div className={classes.root}>
          <form
            className={classes.renameForm}
            onSubmit={(e) => {
              e.preventDefault();
              if (newName) {
                onRename({ view, name: newName });
                setRowState(undefined);
              }
            }}
          >
            <Input
              disableInputAPI
              labelText={locale.get('dataComponents.viewName')}
              name="viewName"
              onChange={(e: ChangeEvent<HTMLInputElement>) => setNewName(e.target.value)}
              screenReaderLabel
              value={newName}
            />
            <div>
              <Button size={EXTRA_SMALL} type="submit" variant={TERTIARY}>
                {locale.get('dataComponents.save')}
              </Button>
              <Button
                className={classes.noButton}
                onClick={() => setRowState(undefined)}
                size={EXTRA_SMALL}
                variant={TERTIARY}
              >
                {locale.get('dataComponents.cancel')}
              </Button>
            </div>
          </form>
        </div>
      );
    case 'deleting':
      return (
        <div className={classnames(classes.root, classes.deleteRoot)}>
          <div>
            <ExclamationTriangleAshIcon />
            <p className={classes.deleteText}>{locale.get('dataComponents.deleteThisView')}</p>
          </div>
          <div>
            <Button
              onClick={() => {
                onDelete({ view });
                setRowState(undefined);
              }}
              size={EXTRA_SMALL}
              variant={TERTIARY}
            >
              {locale.get('dataComponents.yes')}
            </Button>
            <Button
              className={classes.noButton}
              onClick={() => setRowState(undefined)}
              size={EXTRA_SMALL}
              variant={TERTIARY}
            >
              {locale.get('dataComponents.no')}
            </Button>
          </div>
        </div>
      );
    default:
      return (
        <div className={classnames(classes.root, classes.defaultRoot)}>
          <Button
            className={classes.viewButton}
            id={`${view.id}-button`}
            onClick={() => onSetActive()}
            onKeyDown={onViewKeyDown}
            size={EXTRA_SMALL}
            variant={TERTIARY}
          >
            {view.name}
          </Button>
          <div className={classes.rightSide}>
            {view.isDefault && (
              <span className={classes.defaultText} data-qa={`savedViews-default-${view.id}`}>
                {locale.get('dataComponents.default')}
              </span>
            )}
            {(!view.isStatic || !view.isDefault) && (
              <Popover
                className={classes.popover}
                content={popoverContent}
                disableA11yButton
                enablePopoverClick
                getPopoverContainer={popoverRef.current ? () => popoverRef.current! : undefined}
                noArrow
                placement="bottom-end"
                popperOptions={popperOptions}
                zIndex={1001}
              >
                <div>
                  <DotDotDotAshIcon
                    actionable={{
                      'aria-label': locale.get('dataComponents.actions'),
                      id: `${view.id}-action`,
                      onKeyDown: onActionKeyDown,
                    }}
                    className={classes.actionsButton}
                    data-qa={`menu-toggle-${view.id}`}
                    height={12}
                    width={12}
                  />
                </div>
              </Popover>
            )}
          </div>
        </div>
      );
  }
};

export default ViewRow;
